<template>
  <div
    class="modern-container"
    role="main"
    aria-label="Détail de la réclamation livreur"
  >
    <div class="modern-card p-3 p-md-4">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h2 class="m-0">
          Ticket #{{ id }}
        </h2>
        <b-button
          variant="outline-secondary"
          @click="goBack"
        >
          <i class="fas fa-arrow-left mr-1" /> Retour
        </b-button>
      </div>

      <div
        v-if="loading"
        class="text-center py-5"
      >
        <b-spinner
          label="Chargement..."
          class="mb-2"
        />
        <div class="text-muted">
          Chargement du ticket...
        </div>
      </div>

      <div
        v-else-if="error"
        class="alert alert-danger"
      >
        {{ error }}
      </div>

      <div
        v-else-if="ticket"
        class="row"
      >
        <div class="col-12 col-lg-6 mb-3">
          <div class="modern-card p-3">
            <h5 class="mb-3">
              Informations
            </h5>
            <div class="mb-1">
              <strong>Référence:</strong> {{ ticket.reference || '-' }}
            </div>
            <div class="mb-1">
              <strong>Commande:</strong> {{ (ticket.order && (ticket.order.reference || ticket.order.id)) || '-' }}
            </div>
            <div class="mb-1">
              <strong>Livreur:</strong> {{ (ticket.driver && (ticket.driver.full_name || ticket.driver.username)) || '-' }}
            </div>
            <div class="mb-1">
              <strong>Motif:</strong> {{ formatReason(ticket.reason_type) }}
            </div>
            <div class="mb-1"><strong>Statut:</strong>
              <span :class="['status-pill', getStatusClass(ticket.status)]">
                <i :class="getStatusIcon(ticket.status)" /> {{ getStatusLabel(ticket.status) }}
              </span>
            </div>
            <div class="mb-1">
              <strong>Créé le:</strong> {{ formatDate(ticket.created_at) }}
            </div>
            <div class="mb-1">
              <strong>Mis à jour:</strong> {{ formatDate(ticket.updated_at) }}
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-6 mb-3">
          <div class="modern-card p-3">
            <h5 class="mb-3">
              Tentatives de contact
            </h5>
            <div class="d-flex align-items-center mb-2">
              <span class="mr-2"><strong>Total:</strong></span>
              <span
                class="badge badge-pill badge-secondary"
                style="font-size: 0.95rem;"
              >{{ ticket.contact_attempts || 0 }}</span>
            </div>
            <div>
              <strong>Dernière tentative:</strong>
              <span class="text-muted">{{ formatDate(ticket.last_contact_attempt) }}</span>
            </div>
          </div>
        </div>

        <div class="col-12 mb-3">
          <div class="modern-card p-3">
            <h5 class="mb-3">
              Actions
            </h5>
            <div class="form-group">
              <label for="supportResponse"><strong>Note support (optionnelle)</strong></label>
              <textarea
                id="supportResponse"
                v-model="supportResponse"
                class="form-control"
                rows="2"
                placeholder="Ajouter une note pour le livreur..."
              />
            </div>
            <div class="d-flex flex-wrap gap-2">
              <b-button
                variant="primary"
                class="mr-2 mb-2"
                :disabled="actionLoading || ticket.status === 'in_progress'"
                @click="markInProgress"
              >
                <i class="fas fa-play mr-1" /> Prendre en charge
              </b-button>
              <b-button
                variant="outline-primary"
                class="mr-2 mb-2"
                :disabled="actionLoading"
                @click="addContactAttempt"
              >
                <i class="fas fa-phone mr-1" /> Ajouter tentative contact
              </b-button>
              <b-button
                variant="success"
                class="mr-2 mb-2"
                :disabled="actionLoading"
                @click="markResolved"
              >
                <i class="fas fa-check mr-1" /> Résoudre
              </b-button>
              <b-button
                variant="warning"
                class="mr-2 mb-2"
                :disabled="actionLoading"
                @click="escalate"
              >
                <i class="fas fa-exclamation-triangle mr-1" /> Escalader
              </b-button>
              <b-button
                variant="dark"
                class="mb-2"
                :disabled="actionLoading || ticket.status !== 'resolved'"
                @click="markClosed"
              >
                <i class="fas fa-times mr-1" /> Fermer
              </b-button>
              <b-button
                v-if="isAdmin"
                variant="danger"
                class="ml-auto mb-2"
                :disabled="actionLoading"
                @click="confirmDeleteTicket"
              >
                <i class="fas fa-trash mr-1" /> Supprimer
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else
        class="text-muted"
      >
        Aucune donnée à afficher.
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import { BButton, BSpinner } from 'bootstrap-vue'
import storeAuth from '@/store/store'

export default {
  name: 'DriverTicketDetail',
  components: { BButton, BSpinner },
  data() {
    return {
      id: this.$route.params.id,
      ticket: null,
      loading: false,
      error: '',
      actionLoading: false,
      supportResponse: '',
      user: storeAuth.state.user,
    }
  },
  computed: {
    isAdmin() {
      return this.user && this.user.role === 'admin'
    },
  },
  created() {
    this.fetchTicket()
  },
  methods: {
    async confirmDeleteTicket() {
      const message = 'Confirmer la suppression définitive de ce ticket livreur ? Cette action est irréversible.'
      try {
        let ok = true
        if (this.$bvModal && typeof this.$bvModal.msgBoxConfirm === 'function') {
          ok = await this.$bvModal.msgBoxConfirm(message, {
            title: 'Confirmation',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Supprimer',
            cancelTitle: 'Annuler',
            footerClass: 'p-2',
            centered: true,
          })
        } else {
          // eslint-disable-next-line no-alert
          ok = window.confirm(message)
        }
        if (!ok) return
        await axios.delete(`/api/support/driver-tickets/${this.id}/`)
        if (this.$bvToast) this.$bvToast.toast('Ticket supprimé avec succès.', { variant: 'success' })
        this.$router.replace({ name: 'support-driver-ticket-list' })
      } catch (e) {
        const msg = (e.response && e.response.status === 403)
          ? 'Vous n\'avez pas la permission de supprimer ce ticket.'
          : 'Erreur lors de la suppression du ticket.'
        if (this.$bvToast) this.$bvToast.toast(msg, { variant: 'danger' })
      }
    },
    async fetchTicket() {
      this.loading = true
      this.error = ''
      try {
        const { data } = await axios.get(`/api/support/driver-tickets/${this.id}/`)
        this.ticket = data
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load driver ticket', e)
        this.error = 'Impossible de charger le ticket. Veuillez réessayer.'
      } finally {
        this.loading = false
      }
    },
    async patchTicket(payload) {
      this.actionLoading = true
      try {
        await axios.patch(`/api/support/driver-tickets/${this.id}/`, payload)
        await this.fetchTicket()
        if (this.$bvToast) this.$bvToast.toast('Ticket mis à jour', { variant: 'success' })
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Patch driver ticket failed', e)
        if (this.$bvToast) this.$bvToast.toast('Échec de la mise à jour du ticket', { variant: 'danger' })
      } finally {
        this.actionLoading = false
      }
    },
    async markInProgress() {
      if (!this.ticket || this.ticket.status === 'in_progress') return
      await this.patchTicket({ status: 'in_progress' })
    },
    async addContactAttempt() {
      this.actionLoading = true
      try {
        await axios.post(`/api/support/driver-tickets/${this.id}/add-contact-attempt/`)
        await this.fetchTicket()
        if (this.$bvToast) this.$bvToast.toast('Tentative de contact ajoutée', { variant: 'success' })
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Add contact attempt failed', e)
        if (this.$bvToast) this.$bvToast.toast("Échec de l'ajout de tentative", { variant: 'danger' })
      } finally {
        this.actionLoading = false
      }
    },
    async markResolved() {
      await this.patchTicket({ status: 'resolved', support_response: this.supportResponse || undefined })
    },
    async escalate() {
      await this.patchTicket({ status: 'escalated', support_response: this.supportResponse || undefined })
    },
    async markClosed() {
      await this.patchTicket({ status: 'closed' })
    },
    goBack() {
      if (window.history.length > 1) this.$router.back()
      else this.$router.push({ name: 'support-driver-ticket-list' })
    },
    formatReason(reason) {
      if (!reason) return '-'
      if (typeof reason === 'object') return reason.label || reason.name || reason.title || reason.slug || '-'
      return reason
    },
    getStatusClass(status) {
      if (!status) return 'status-open'
      const s = (status || '').toString().toLowerCase()
      if (s === 'new') return 'status-open'
      if (s === 'in_progress') return 'status-progress'
      if (s === 'resolved') return 'status-resolved'
      if (s === 'closed') return 'status-closed'
      if (s === 'escalated') return 'status-escalated'
      return 'status-open'
    },
    getStatusIcon(status) {
      if (!status) return 'fas fa-clock'
      const s = (status || '').toString().toLowerCase()
      if (s === 'new') return 'fas fa-bell'
      if (s === 'in_progress') return 'fas fa-spinner'
      if (s === 'resolved') return 'fas fa-check'
      if (s === 'closed') return 'fas fa-times'
      if (s === 'escalated') return 'fas fa-exclamation-triangle'
      return 'fas fa-clock'
    },
    getStatusLabel(status) {
      if (!status) return '-'
      const s = (status || '').toString().toLowerCase()
      if (s === 'new') return 'Nouveau'
      if (s === 'in_progress') return 'En cours'
      if (s === 'resolved') return 'Résolu'
      if (s === 'closed') return 'Fermé'
      if (s === 'escalated') return 'Escaladé'
      return status
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('fr-FR', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.modern-container {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 100vh;
  padding: 2rem 1.5rem;
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}
.modern-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}
.modern-card:hover {
  box-shadow: 0 8px 24px rgba(13, 67, 153, 0.08);
}
.status-pill { display: inline-flex; align-items: center; gap: .5rem; padding: .4rem .8rem; border-radius: 999px; font-size: .8rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;}
.status-open { background: linear-gradient(135deg, #090909 0%, #1a202c 100%); color: white; }
.status-progress { background: rgba(91, 166, 227, 0.68); color: white; }
.status-resolved { background: #2FDD6F; color: white; }
.status-closed { background: linear-gradient(135deg, #0d4399 0%, #0d4399 100%); color: white; }
.status-escalated { background: linear-gradient(135deg, #ff9f43 0%, #ff8c00 100%); color: white; }

h2 {
  font-weight: 700;
  color: #090909;
}
h5 {
  font-weight: 600;
  color: #0d4399;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 0.75rem;
  margin-bottom: 1rem;
}
::v-deep .btn-primary {
  background: linear-gradient(135deg, #0d4399 0%, #4a90e2 100%) !important;
  border: none !important;
}
::v-deep .btn-primary:hover:not(:disabled) {
  box-shadow: 0 4px 12px rgba(13, 67, 153, 0.4) !important;
  transform: translateY(-1px) !important;
}
::v-deep .btn-outline-primary {
  color: #0d4399 !important;
  border-color: #0d4399 !important;
}
::v-deep .btn-outline-primary:hover:not(:disabled) {
  background: #0d4399 !important;
  color: white !important;
}
::v-deep .btn-outline-secondary {
  border-radius: 8px;
  font-weight: 500;
}
</style>
